import { formatBytes } from "@/lib/format";

/** The most bytes one upload takes. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * The kinds of file the resume service reads, by extension. The picker
 * offers only these, but a dropped file skips the picker, so the name is
 * checked again before anything is sent.
 */
export const UPLOAD_EXTENSIONS = ["pdf", "docx", "md", "markdown", "toml", "txt"] as const;

/** The picker's `accept`: ".pdf,.docx,.md,.markdown,.toml,.txt". */
export const UPLOAD_ACCEPT = UPLOAD_EXTENSIONS.map((extension) => `.${extension}`).join(",");

function extensionOf(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

/** Why a chosen file cannot be sent; null when it can. */
export function fileProblem(file: File): string | null {
    if (!(UPLOAD_EXTENSIONS as readonly string[]).includes(extensionOf(file.name)))
        return "Job Envoy reads PDF, Word, Markdown, TOML and plain text files.";
    if (file.size === 0) return "That file is empty.";
    if (file.size > MAX_UPLOAD_BYTES) return "That file is over 10 MB.";
    return null;
}

/** "PDF", "DOCX", "MD", "TOML", "TXT" from the name, for the file's tile. */
export function fileBadge(name: string): string {
    const dot = name.lastIndexOf(".");
    const extension = dot < 0 ? "" : name.slice(dot + 1).toUpperCase();
    return extension && extension.length <= 4 ? extension : "FILE";
}

/** "184 KB · chosen just now": what the browser knows before the file is sent. */
export function chosenLine(file: File, pages: number | null | undefined): string {
    return [
        formatBytes(file.size),
        pages ? `${pages} ${pages === 1 ? "page" : "pages"}` : null,
        "chosen just now",
    ]
        .filter(Boolean)
        .join(" · ");
}
